import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Colors, transparentize } from '../Style/Colors';
import Portal from './Portal';

export interface ModalProps {
	children?: React.ReactNode; // Overwrite modal content
	showClose?: boolean;
	allowWrapperClickToClose?: boolean;
	onClose?: () => void;
	maxWidth?: string;
	content?: JSX.Element;
	open?: boolean;
	className?: string;
	zIndex?: number;
	topOffset?: number;
}

export interface ModalContentProps {
	title?: string;
	subtitle?: string;
	text?: string;
	headerImg?: string;
	body?: React.ReactNode;
	footer?: React.ReactNode;
	actions?: ModalAction[];
}

interface ModalAction {
	text?: string /** Text to display inside button */;
	callback?: () => void;
	child?: React.ReactNode;
}

interface ModalStyle {
	opacity: string;
	position: number;
}

const RestingModalStyle = {
	opacity: '0',
	position: -50,
};

const OpenModalStyle = {
	opacity: '1',
	position: 0,
};

export default function Modal(props: ModalProps) {
	const [open, setOpen] = useState<boolean>();
	const [style, setStyle] = useState<ModalStyle>(RestingModalStyle);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const showClose = props.showClose === undefined ? true : props.showClose;
	const allowWrapperClickToClose =
		props.allowWrapperClickToClose === undefined ? true : props.allowWrapperClickToClose;

	const className = props.className ? props.className : '';
	const zIndex = props.zIndex !== undefined ? props.zIndex : 500;
	const topOffset = props.topOffset !== undefined ? props.topOffset : 0;

	const handleCloseClick = (e: React.MouseEvent | MouseEvent) => {
		e.stopPropagation();
		props.onClose?.();
	};

	const handleWrapperClick = (e: MouseEvent) => {
		if (allowWrapperClickToClose === true) {
			handleCloseClick(e);
		}
	};

	useEffect(() => {
		if (props.open) {
			setOpen(true);
			const timeout = setTimeout(() => {
				setStyle(OpenModalStyle);
			}, 50);

			return () => {
				clearTimeout(timeout);
			};
		} else {
			setStyle(RestingModalStyle);
			const timeout = setTimeout(() => {
				setOpen(false);
			}, 250);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [props.open]);

	const attachMouseUp = (e: MouseEvent) => {
		if (containerRef.current?.contains(e.target as Node)) return;
		handleWrapperClick(e);
	};

	useEffect(() => {
		const currentWrapper = wrapperRef.current;
		currentWrapper?.addEventListener('mousedown', attachMouseUp);
		return () => {
			currentWrapper?.removeEventListener('mousedown', attachMouseUp);
		};
	});

	if (open) {
		return (
			<Portal>
				<ModalWrapper
					modalStyle={style}
					ref={wrapperRef}
					className={className}
					topOffset={topOffset}
					zIndex={zIndex}
				>
					<TopMargin />
					<ModalFrameContainer ref={containerRef}>
						{showClose && <ModalClose onClick={handleCloseClick} className={'fas fa-times'} />}
						{props.children}
					</ModalFrameContainer>
				</ModalWrapper>
			</Portal>
		);
	} else {
		return null;
	}
}

const TopMargin = styled.div`
	height: 7rem;
	flex-shrink: 1;
	flex-grow: 0;
`;

const ModalFrameContainer = styled.div`
	position: relative;
	z-index: 1001;
	margin: 0 auto;
	width: fit-content;

	@media (max-width: 600px) {
		margin: 0;
	}
`;

const ModalClose = styled.i`
	color: ${Colors.gray7};
	background: transparent;
	border: none;
	position: absolute;
	top: 8px;
	right: 20px;
	cursor: pointer;
	font-size: 16px;
	z-index: 1;
	&:hover {
		color: ${transparentize(Colors.white, 0.3)};
	}
`;

const ModalWrapper = styled.div<{ modalStyle: ModalStyle; topOffset: number; zIndex: number }>`
	position: fixed;
	z-index: ${(props) => props.zIndex};
	width: 100vw;
	height: calc(var(--vh) * 100 - ${(props) => props.topOffset}px);
	top: ${(props) => props.topOffset}px;
	left: 0;
	display: flex;
	flex-direction: column;
	background-color: rgba(21, 21, 23, 0.4);
	opacity: ${(props) => props.modalStyle.opacity};
	transition: opacity 250ms;
	${ModalFrameContainer} {
		transform: translateY(${(props) => props.modalStyle.position}px);
		transition: transform 250ms;
	}
`;
